import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { getConsent, initConsent, setConsent, subscribe } from "../lib/consent";

/**
 * CookieConsent — the banner (first visit) and the preferences dialog
 * ("consent center", opened from the footer or the Privacy Policy).
 * Mounted once in App.js. Analytics only loads after an explicit yes.
 */
export const CookieConsent = () => {
  const [decided, setDecided] = useState(true);   // true until mount so nothing flashes
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    initConsent();
    const current = getConsent();
    setDecided(Boolean(current));
    setAnalytics(Boolean(current?.analytics));
    return subscribe((evt) => {
      if (evt.type === "open") {
        setAnalytics(Boolean(getConsent()?.analytics));
        setPrefsOpen(true);
      }
      if (evt.type === "change") {
        setDecided(true);
        setAnalytics(evt.consent.analytics);
      }
    });
  }, []);

  const savePrefs = () => {
    setConsent({ analytics, source: "preferences" });
    setPrefsOpen(false);
    toast.success(analytics ? "Analytics cookies on." : "Essential cookies only.", {
      description: "Change this anytime from “Cookie preferences” in the footer.",
    });
  };

  return (
    <>
      <AnimatePresence>
        {!decided && !prefsOpen && (
          <motion.aside
            role="dialog"
            aria-label="Cookie notice"
            data-testid="cookie-banner"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-4 bottom-4 z-[95] rounded-2xl border border-border bg-bone p-5 shadow-xl sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md"
          >
            <p className="font-serif text-3xl leading-none text-ink">A quick word on cookies.</p>
            <p className="mt-3 text-[13px] leading-relaxed text-olive">
              Essential cookies keep sign-in and giving working. With your OK we also use Google Analytics 4
              to see which pages help, so we can improve them. Nothing is sold, ever.{" "}
              <Link to="/privacy#google-analytics" className="underline decoration-ink/30 underline-offset-2 hover:text-ink" data-testid="cookie-banner-privacy-link">
                Privacy Policy
              </Link>
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setConsent({ analytics: true })}
                data-testid="cookie-accept-analytics"
                className="inline-flex items-center rounded-full bg-ink px-4 py-2 text-[13px] font-semibold text-sand transition-colors hover:bg-forest"
              >
                Accept analytics
              </button>
              <button
                type="button"
                onClick={() => setConsent({ analytics: false })}
                data-testid="cookie-essential-only"
                className="inline-flex items-center rounded-full border border-ink/25 bg-bone px-4 py-2 text-[13px] font-semibold text-ink transition-colors hover:border-ink"
              >
                Essential only
              </button>
              <button
                type="button"
                onClick={() => setPrefsOpen(true)}
                data-testid="cookie-open-preferences"
                className="ml-auto text-[13px] font-semibold text-olive underline-offset-4 hover:text-ink hover:underline"
              >
                Preferences
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="max-w-md border-border bg-sand" data-testid="cookie-preferences-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-normal text-ink">Cookie preferences</DialogTitle>
            <DialogDescription className="text-[13px] text-olive">
              Choose what My Rich Aunt may store on this device. Your choice is kept for 12 months and can be changed here anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-bone p-4">
              <div>
                <p className="text-[13px] font-semibold text-ink">Essential</p>
                <p className="mt-0.5 text-[13px] text-olive">Sign-in, security, and remembering this choice. Always on.</p>
              </div>
              <Switch checked disabled aria-label="Essential cookies (always on)" data-testid="cookie-pref-essential" />
            </div>
            <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-bone p-4">
              <div>
                <p className="text-[13px] font-semibold text-ink">Analytics (Google Analytics 4)</p>
                <p className="mt-0.5 text-[13px] text-olive">
                  Pages viewed, time on page, scroll depth and clicks, so we can see what helps donors and students. IPs anonymized; data processed by Google LLC.
                </p>
              </div>
              <Switch checked={analytics} onCheckedChange={(v) => setAnalytics(Boolean(v))} aria-label="Analytics cookies" data-testid="cookie-pref-analytics" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <Link to="/privacy#google-analytics" onClick={() => setPrefsOpen(false)} className="self-center text-[13px] text-olive underline-offset-4 hover:text-ink hover:underline" data-testid="cookie-prefs-privacy-link">
              Read the Privacy Policy
            </Link>
            <button
              type="button"
              onClick={savePrefs}
              data-testid="cookie-prefs-save"
              className="inline-flex items-center rounded-full bg-ink px-5 py-2.5 text-[13px] font-semibold text-sand transition-colors hover:bg-forest"
            >
              Save preferences
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
